import { Building2, CalendarDays, Home, type LucideIcon, MessageSquare, Sparkles, Users, Video } from "lucide-react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "motion/react";
import { type PointerEvent, type ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MOCK_COMMUNITY } from "@/src/common/components/app-mock/mock-community";
import { WebAiInsightsScreen } from "@/src/common/components/app-mock/web-screens/web-ai-screen";
import { WebCommunitySettingsScreen } from "@/src/common/components/app-mock/web-screens/web-community-settings-screen";
import { WebEventsScreen } from "@/src/common/components/app-mock/web-screens/web-events-screen";
import { FeedRightSidebar, WebFeedScreen } from "@/src/common/components/app-mock/web-screens/web-feed-screen";
import { WebMeetingsScreen } from "@/src/common/components/app-mock/web-screens/web-meetings-screen";
import { WebMembersScreen } from "@/src/common/components/app-mock/web-screens/web-members-screen";
import { WebMessagesScreen } from "@/src/common/components/app-mock/web-screens/web-messages-screen";
import { WEB_NAV_PATH, WebAppShell, type WebNavId } from "@/src/common/components/app-mock/web-shell";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

type DemoTabId = "home" | "members" | "messages" | "meetings" | "events" | "ai" | "settings";

interface DemoTab {
	id: DemoTabId;
	nav: WebNavId;
	icon: LucideIcon;
	fullBleed?: boolean;
	render: () => ReactElement;
	rightSidebar?: () => ReactElement;
}

const HOME_TAB: DemoTab = {
	id: "home",
	nav: "feed",
	icon: Home,
	render: () => <WebFeedScreen interactive />,
	rightSidebar: () => <FeedRightSidebar />,
};

const TABS: ReadonlyArray<DemoTab> = [
	HOME_TAB,
	{ id: "members", nav: "members", icon: Users, render: () => <WebMembersScreen interactive /> },
	{ id: "messages", nav: "messages", icon: MessageSquare, fullBleed: true, render: () => <WebMessagesScreen interactive /> },
	{ id: "meetings", nav: "meetings", icon: Video, render: () => <WebMeetingsScreen interactive /> },
	{ id: "events", nav: "events", icon: CalendarDays, render: () => <WebEventsScreen interactive /> },
	{ id: "ai", nav: "admin-ai", icon: Sparkles, fullBleed: true, render: () => <WebAiInsightsScreen interactive /> },
	{ id: "settings", nav: "admin-community", icon: Building2, render: () => <WebCommunitySettingsScreen interactive /> },
];

const NAVIGABLE: ReadonlyArray<WebNavId> = TABS.map((tab) => tab.nav);
const PANEL_ID = "product-demo-panel";
const TOUR_INTERVAL_MS = 5000;

function findTabByNav(nav: WebNavId): DemoTab {
	return TABS.find((tab) => tab.nav === nav) ?? HOME_TAB;
}

function nextTabId(current: DemoTabId): DemoTabId {
	const index = TABS.findIndex((tab) => tab.id === current);
	const next = TABS[(index + 1) % TABS.length];
	return next ? next.id : HOME_TAB.id;
}

interface ProductDemoProps {
	autoTour?: boolean;
}

export function ProductDemo({ autoTour = true }: ProductDemoProps) {
	const { t } = useTranslation("home");
	const { palette } = useCommunityVariant();
	const reduced = useReducedMotion() === true;
	const rootRef = useRef<HTMLDivElement>(null);
	const inView = useInView(rootRef, { amount: 0.5 });
	const [activeId, setActiveId] = useState<DemoTabId>("home");
	const [tourEnabled, setTourEnabled] = useState(autoTour);
	const touring = tourEnabled && inView && !reduced;
	const activeTab = TABS.find((tab) => tab.id === activeId) ?? HOME_TAB;
	const RightSidebar = activeTab.rightSidebar;
	const path = WEB_NAV_PATH[activeTab.nav];

	useEffect(() => {
		if (!touring) return;
		const timeout = window.setTimeout(() => setActiveId(nextTabId(activeId)), TOUR_INTERVAL_MS);
		return () => window.clearTimeout(timeout);
	}, [touring, activeId]);

	const stopTour = () => setTourEnabled(false);
	const handlePointerEnter = (event: PointerEvent<HTMLDivElement>) => {
		if (event.pointerType === "mouse") stopTour();
	};

	return (
		<div
			ref={rootRef}
			className="overflow-hidden rounded-2xl border border-default-200 bg-white shadow-lift"
			data-mock="product-demo"
			onPointerDownCapture={stopTour}
			onKeyDownCapture={stopTour}
			onFocusCapture={stopTour}
			onPointerEnter={handlePointerEnter}
		>
			<div className="relative flex items-center gap-3 border-b border-default-200/80 bg-content1 px-3 py-2.5">
				<div className="flex items-center gap-1.5" aria-hidden="true">
					<span className="size-2.5 rounded-full bg-red-400" />
					<span className="size-2.5 rounded-full bg-yellow-400" />
					<span className="size-2.5 rounded-full bg-green-400" />
				</div>
				<div
					className="hidden items-center gap-1.5 rounded-md bg-white px-2 py-1 text-[10px] font-medium text-default-600 sm:flex"
					aria-hidden="true"
				>
					<span className="size-2 rounded-full" style={{ background: palette.accent }} />
					<span className="truncate">{MOCK_COMMUNITY.name}</span>
				</div>
				<div className="flex min-w-0 flex-1 items-center justify-center rounded-md border border-default-200/60 bg-content2 px-3 py-1 text-xs font-medium text-default-500">
					<span className="truncate">
						{MOCK_COMMUNITY.domain}
						<span className="text-default-400">{path}</span>
					</span>
				</div>
				<span className="hidden items-center gap-1.5 rounded-full border border-default-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-default-500 sm:inline-flex">
					<motion.span
						aria-hidden="true"
						className="size-1.5 rounded-full bg-emerald-500"
						animate={reduced ? undefined : { opacity: [1, 0.35, 1] }}
						transition={reduced ? undefined : { duration: 1.8, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
					/>
					{t("demo.live_badge")}
				</span>
				{touring ? (
					<motion.span
						key={activeId}
						aria-hidden="true"
						className="absolute inset-x-0 bottom-0 h-0.5 origin-left"
						style={{ background: palette.accent }}
						initial={{ scaleX: 0 }}
						animate={{ scaleX: 1 }}
						transition={{ duration: TOUR_INTERVAL_MS / 1000, ease: "linear" }}
					/>
				) : null}
			</div>

			<div
				role="tablist"
				aria-label={t("demo.nav_label")}
				className="mock-scroll flex items-center gap-1 overflow-x-auto border-b border-neutral-100 bg-white px-2 py-1.5 md:hidden"
			>
				{TABS.map((tab) => {
					const isActive = tab.id === activeId;
					const Icon = tab.icon;
					return (
						<button
							key={tab.id}
							type="button"
							role="tab"
							id={`tab-mobile-${tab.id}`}
							aria-selected={isActive}
							aria-controls={PANEL_ID}
							onClick={() => setActiveId(tab.id)}
							className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-medium transition ${
								isActive ? "" : "text-neutral-500 hover:bg-neutral-100"
							}`}
							style={isActive ? { background: palette.accentSoftHover, color: palette.accentInk } : undefined}
						>
							<Icon className="size-3.5" />
							{t(`demo.tabs.${tab.id}.label`)}
						</button>
					);
				})}
			</div>

			<div className="h-[480px] md:h-[600px]">
				<WebAppShell
					activeNav={activeTab.nav}
					onNavigate={(nav) => setActiveId(findTabByNav(nav).id)}
					navigable={NAVIGABLE}
					panelId={PANEL_ID}
					navLabel={t("demo.nav_label")}
					fullBleed={activeTab.fullBleed}
					rightSidebar={RightSidebar ? <RightSidebar /> : undefined}
				>
					<AnimatePresence mode="wait" initial={false}>
						<motion.div
							key={activeId}
							role="tabpanel"
							id={PANEL_ID}
							aria-labelledby={`tab-${activeTab.nav} tab-mobile-${activeTab.id}`}
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -4 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							className="demo-panel-enter h-full min-h-0"
						>
							{activeTab.render()}
						</motion.div>
					</AnimatePresence>
				</WebAppShell>
			</div>

			<footer className="flex items-center justify-between gap-3 border-t border-default-200/60 bg-content1/40 px-4 py-2.5 text-[11px] text-default-500">
				<span className="inline-flex min-w-0 items-center gap-1.5">
					<span className="size-1.5 shrink-0 rounded-full bg-emerald-500 animate-glow" />
					<span className="truncate">
						<span className="font-semibold text-default-700">{t(`demo.tabs.${activeId}.label`)}</span> · {t(`demo.tabs.${activeId}.hint`)}
					</span>
				</span>
				<span className="hidden shrink-0 font-mono text-[10px] text-default-400 sm:inline">v2.0 · {path}</span>
			</footer>
		</div>
	);
}
